// EmployeeTimesheetBulk.jsx
import React, { Component } from "react";
import { Table, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import CartContext from "./CartContext";
import Swal from "sweetalert2";

const API_BASE = process.env.REACT_APP_API + "EmployeeTimesheet";

/**
 Props:
  - storeId: GUID string (required)
*/
export default class EmployeeTimesheet extends Component {
  static contextType = CartContext;
  constructor(props) {
    super(props);
    const today = new Date();
    this.state = {
      viewMode: "daily", // daily | weekly | monthly
      selectedDate: this.toISODate(today),
      employees: [], // [{ id, firstName }]
      timesheetData: {}, // { `${empId}-${yyyy-MM-dd}`: "8" (string) or "" }
      originalData: {},
      bulkHours: "",
      applyBulkToEmptyOnly: true,
      loading: false,
      saving: false,
      error: "",
    };
  }

  /* -----------------------
     Date helpers
  ----------------------- */
  toISODate(d) {
    const dt = d instanceof Date ? d : new Date(d);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  fromISO(iso) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  startOfWeek(date) {
    const d = new Date(date);
    const day = d.getDay(); // 0..6 (Sun..Sat)
    const diff = day === 0 ? -6 : 1 - day; // monday
    const monday = new Date(d);
    monday.setDate(d.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }
  endOfWeek(date) {
    const s = this.startOfWeek(date);
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    e.setHours(23, 59, 59, 999);
    return e;
  }
  startOfMonth(date) {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  endOfMonth(date) {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }
  eachDay(start, end) {
    const days = [];
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    while (cur <= end) {
      days.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  }
  toUnixMillisecondsAtUtcMidnight(iso) {
    const d = this.fromISO(iso);
    return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /* -----------------------
     API helpers
  ----------------------- */
  apiGetByDay = async (storeId, isoDate) => {
    const ms = this.context.GetTicks(new Date(isoDate));
    const url = `${API_BASE}/GetEmployeesTimesheetByDay/${storeId}/${ms}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`GetByDay failed: ${res.statusText}`);
    return res.json();
  };
  apiGetByMonth = async (storeId, year, month) => {
    const url = `${API_BASE}/GetEmployeesTimesheetByMonth/${storeId}/${year}/${month}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`GetByMonth failed: ${res.statusText}`);
    return res.json();
  };
  apiSaveByMonth = async (storeId, year, month, payload) => {
    const url = `${API_BASE}/SaveEmployeesTimesheetByMonth/${storeId}/${year}/${month}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Save failed: ${res.status} ${text}`);
    }
    return res.json?.();
  };

  /* -----------------------
     Loaders
  ----------------------- */
  componentDidMount() {
    this.loadGrid();
    window.scrollTo(0, 0);
  }
  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.viewMode !== this.state.viewMode ||
      prevState.selectedDate !== this.state.selectedDate ||
      prevProps.storeId !== this.props.storeId
    ) {
      this.loadGrid();
    }
  }

  async loadGrid() {
    const { viewMode, selectedDate } = this.state;
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "storeId prop is required" });
      return;
    }

    this.setState({ loading: true, error: "" });

    try {
      if (viewMode === "daily") {
        const rows = await this.apiGetByDay(storeId, selectedDate);
        this.normalizeAndSet(rows, [this.fromISO(selectedDate)]);
      } else if (viewMode === "weekly") {
        const base = this.fromISO(selectedDate);
        const weekStart = this.startOfWeek(base);
        const weekEnd = this.endOfWeek(base);
        const days = this.eachDay(weekStart, weekEnd);
        const promises = days.map((d) =>
          this.apiGetByDay(storeId, this.toISODate(d))
        );
        const results = await Promise.all(promises);
        const flattened = [];
        for (const arr of results) {
          arr.forEach((r) => {
            flattened.push({
              EmployeeId: r.id,
              FirstName: r.firstName,
              TimesheetDate: r.timesheetDate,
              TotalHours: r.totalHours ?? null,
            });
          });
        }
        this.normalizeAndSet(flattened, days);
      } else {
        // monthly
        const d = this.fromISO(selectedDate);
        const year = d.getFullYear();
        const month = d.getMonth() + 1;
        const rows = await this.apiGetByMonth(storeId, year, month);
        const start = this.startOfMonth(d);
        const end = this.endOfMonth(d);
        const days = this.eachDay(start, end);
        const mapped = rows.map((r) => ({
          EmployeeId: r.EmployeeId ?? r.employeeId ?? r.Id ?? r.id,
          FirstName: r.FirstName ?? r.firstName,
          TimesheetDate: r.TimesheetDate ?? r.timesheetDate,
          TotalHours: r.TotalHours ?? r.totalHours ?? null,
        }));
        this.normalizeAndSet(mapped, days);
      }

      this.setState({ loading: false });
    } catch (err) {
      this.setState({
        loading: false,
        error: err.message || "Failed to load data",
      });
    }
    window.scrollTo(0, 0);
  }

  safeISO = (val) => {
    if (!val) return "";
    // If API returns "YYYY-MM-DD..." just take the date part
    if (typeof val === "string") {
      const m = val.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    // Fallback
    return this.toISODate(new Date(val));
  };

  normalizeAndSet(apiRows, visibleDaysArray) {
    const employeesMap = new Map();
    const tsMap = {};

    (apiRows || []).forEach((r) => {
      const empId = r.EmployeeId ?? r.Id ?? r.id;
      const firstName = r.FirstName ?? r.firstName;
      if (!employeesMap.has(empId))
        employeesMap.set(empId, { id: empId, firstName });

      // const iso = this.toISODate(new Date(r.TimesheetDate ?? r.timesheetDate));
      const iso = this.safeISO(r.TimesheetDate ?? r.timesheetDate);
      const key = `${empId}-${iso}`;

      // const hours = r.TotalHours ?? r.totalHours;
      // tsMap[key] = hours ? hours.toString() : ""; // <-- 0 becomes ""
      const hours = r.TotalHours ?? r.totalHours;
      tsMap[key] = hours === null || hours === undefined ? "" : String(hours); // keep "0"
    });

    const employees = Array.from(employeesMap.values());
    const dates = visibleDaysArray ?? this.getVisibleRangeDates();

    for (const emp of employees) {
      for (const d of dates) {
        const iso = this.toISODate(d);
        const key = `${emp.id}-${iso}`;
        if (!(key in tsMap)) tsMap[key] = "";
      }
    }

    this.setState({
      employees,
      timesheetData: tsMap,
      originalData: { ...tsMap },
    });
  }

  getVisibleRangeDates() {
    const { viewMode, selectedDate } = this.state;
    const base = this.fromISO(selectedDate);
    if (viewMode === "daily") return [base];
    if (viewMode === "weekly") {
      const start = this.startOfWeek(base);
      const end = this.endOfWeek(base);
      return this.eachDay(start, end);
    }
    const start = this.startOfMonth(base);
    const end = this.endOfMonth(base);
    return this.eachDay(start, end);
  }

  /* -----------------------
     Editing / Bulk apply / Save
  ----------------------- */
  handleCellChange = (empId, isoDate, value) => {
    if (value === "") {
      this.setState((s) => ({
        timesheetData: { ...s.timesheetData, [`${empId}-${isoDate}`]: "" },
      }));
      return;
    }
    const num = Number(value);
    if (Number.isNaN(num) || num < 0 || num > 24) return;
    this.setState((s) => ({
      timesheetData: {
        ...s.timesheetData,
        [`${empId}-${isoDate}`]: String(value),
      },
    }));
  };

  applyBulk = () => {
    const { bulkHours, applyBulkToEmptyOnly } = this.state;
    if (bulkHours === "") return;
    const num = Number(bulkHours);
    if (Number.isNaN(num) || num < 0 || num > 24) return;

    const dates = this.getVisibleRangeDates();
    const updates = { ...this.state.timesheetData };

    for (const emp of this.state.employees) {
      for (const d of dates) {
        const iso = this.toISODate(d);
        const key = `${emp.id}-${iso}`;

        // ✅ Skip Sundays
        const day = new Date(iso).getDay(); // 0 = Sunday
        if (day === 0) continue;

        if (applyBulkToEmptyOnly) {
          if (updates[key] === "" || updates[key] == null) {
            updates[key] = String(bulkHours);
          }
        } else {
          updates[key] = String(bulkHours);
        }
      }
    }

    this.setState({ timesheetData: updates });
  };

  saveAll = async () => {
    const { timesheetData, originalData, employees } = this.state;
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "storeId prop required" });
      return;
    }

    const changed = [];
    for (const emp of employees) {
      const dates = this.getVisibleRangeDates();
      for (const d of dates) {
        const iso = this.toISODate(d);
        const key = `${emp.id}-${iso}`;
        const cur = String(timesheetData[key] ?? "");
        const prev = String(originalData[key] ?? "");
        if (cur !== prev) {
          if (cur === "") continue; // skip clears
          changed.push({
            employeeId: emp.id,
            timesheetDate: iso,
            totalHours: parseFloat(cur),
          });
        }
      }
    }

    if (changed.length === 0) {
      this.setState({ error: "No changes to save." });
      setTimeout(() => this.setState({ error: "" }), 1500);
      return;
    }

    const groups = {};
    changed.forEach((c) => {
      const dt = new Date(c.timesheetDate);
      const year = dt.getFullYear();
      const month = dt.getMonth() + 1;
      const key = `${year}-${month}`;
      if (!groups[key]) groups[key] = { year, month, items: [] };
      groups[key].items.push({
        EmployeeId: c.employeeId,
        TimesheetDate: c.timesheetDate,
        TotalHours: c.totalHours,
      });
    });

    this.setState({ saving: true, error: "" });
    try {
      for (const gk of Object.keys(groups)) {
        const g = groups[gk];
        await this.apiSaveByMonth(storeId, g.year, g.month, g.items);
      }
      this.setState(
        {
          originalData: { ...this.state.timesheetData },
          saving: false,
        },
        () => {
          Swal.fire({
            title: "Information",
            text: "Saved Successfully",
            timer: 1000,
          });
        }
      );
    } catch (err) {
      this.setState({ saving: false, error: err.message || "Save failed" });
    }
  };

  /* -----------------------
     UI
  ----------------------- */
  renderControls() {
    const { viewMode, selectedDate, saving, bulkHours, applyBulkToEmptyOnly } =
      this.state;
    return (
      <div>
        <h3 className="text-start">
          <b>Employee Timesheet</b>
        </h3>

        <div
          className="d-flex flex-wrap align-items-start gap-3 mb-3 p-2 bg-info CornersRoundedSmall"
          style={{ maxWidth: "100%", overflow: "hidden" }}
        >
          {/* Select Date */}
          <div className="d-flex flex-column align-items-start flex-shrink-0">
            <label className="mb-1 fw-bold">Select Date</label>
            <div className="d-flex align-items-center gap-1">
              <Button variant="outline-secondary" onClick={this.goPrev}>
                ◀
              </Button>
              <Form.Control
                type="date"
                value={this.state.selectedDate}
                onChange={(e) =>
                  this.setState({ selectedDate: e.target.value })
                }
                style={{ maxWidth: "140px", flexShrink: 0 }}
              />
              <Button variant="outline-secondary" onClick={this.goNext}>
                ▶
              </Button>
            </div>
          </div>

          {/* View Selector */}
          <div className="d-flex flex-column flex-shrink-0 mx-5">
            <label className="mb-1 fw-bold">View</label>
            <Form.Select
              value={this.state.viewMode}
              onChange={(e) =>
                this.setState({ viewMode: e.target.value }, this.loadGrid)
              }
              style={{ maxWidth: "120px" }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </div>

          {/* Bulk Edit */}
          <div className="d-flex flex-column flex-shrink-0 border CornersRoundedSmall p-2">
            <label className="mb-1 fw-bold">
              Bulk Edit (applies to visible range)
            </label>
            <div className="d-flex  align-items-center gap-2">
              <Form.Control
                type="number"
                min={0}
                max={24}
                step={0.25}
                placeholder="Hours"
                value={this.state.bulkHours}
                onChange={(e) => this.setState({ bulkHours: e.target.value })}
                style={{ maxWidth: "100px" }}
              />
              <Form.Check
                type="checkbox"
                label="Empty only"
                checked={this.state.applyBulkToEmptyOnly}
                onChange={(e) =>
                  this.setState({ applyBulkToEmptyOnly: e.target.checked })
                }
              />
              <Button variant="warning" onClick={this.applyBulk}>
                Apply
              </Button>
            </div>
          </div>
          <div className="d-flex flex-column flex-shrink-0  CornersRoundedSmall p-2 mt-3">
            <Button
              variant="success"
              size="lg"
              disabled={this.state.saving}
              onClick={this.saveAll}
            >
              {this.state.saving ? "Saving…" : "Save All"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  goPrev = () => {
    const d = this.fromISO(this.state.selectedDate);
    if (this.state.viewMode === "daily") d.setDate(d.getDate() - 1);
    else if (this.state.viewMode === "weekly") d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    this.setState({ selectedDate: this.toISODate(d) });
  };
  goNext = () => {
    const d = this.fromISO(this.state.selectedDate);
    if (this.state.viewMode === "daily") d.setDate(d.getDate() + 1);
    else if (this.state.viewMode === "weekly") d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    this.setState({ selectedDate: this.toISODate(d) });
  };

  getEmployeeTotal(empId, dates) {
    const { timesheetData } = this.state;
    let total = 0;

    for (const d of dates) {
      const iso = this.toISODate(d); // your normalized YYYY-MM-DD
      const raw = timesheetData[`${empId}-${iso}`];

      if (raw !== undefined && raw !== "") {
        const num = Number(raw);
        if (!Number.isNaN(num)) {
          total += num;
        }
      }
    }

    return Math.round(total * 100) / 100; // round to 2 decimals
  }

  formatHours(h) {
    if (h == null) return ""; // <-- show empty if null
    return Number.isInteger(h) ? `${h}` : h.toFixed(2);
  }

  renderTable() {
    const dates = this.getVisibleRangeDates();
    const { employees, timesheetData } = this.state;

    return (
      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <Table bordered hover size="sm" className="align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ minWidth: 240 }}>Employee (Total Hours)</th>
              {dates.map((d) => {
                const iso = this.toISODate(d);
                const dayName = d.toLocaleDateString(undefined, {
                  weekday: "short",
                });
                const label = d.toLocaleDateString(undefined, {
                  day: "2-digit",
                  month: "short",
                });
                return (
                  <th
                    key={iso}
                    className="text-center"
                    style={{ minWidth: 60 }}
                  >
                    <div>{dayName}</div>
                    <div className="small text-muted">{label}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => {
              const total = this.getEmployeeTotal(emp.id, dates);
              return (
                <tr key={emp.id}>
                  <td>
                    {emp.firstName}{" "}
                    <span className="fw-bold">({this.formatHours(total)})</span>
                  </td>
                  {dates.map((d) => {
                    const iso = this.toISODate(d);
                    const key = `${emp.id}-${iso}`;
                    const isSunday = new Date(iso).getDay() === 0; // Sunday check

                    return (
                      <td key={key}>
                        {isSunday ? (
                          <span className="text-muted">Holiday</span>
                        ) : (
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0}
                            max={24}
                            step={0.25}
                            value={timesheetData[key] || ""}
                            onChange={(e) =>
                              this.handleCellChange(emp.id, iso, e.target.value)
                            }
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  render() {
    const { loading, error } = this.state;
    return (
      <div className="p-2">
        {this.renderControls()}
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          this.renderTable()
        )}
      </div>
    );
  }
}
